import axios, { AxiosError, AxiosInstance } from "axios";
import { JwtStore } from "./JwtStore";
import { AccessTokenResponse } from "./protocol";

/**
 * Machine-readable reason the BE puts in a terminal-401 body when this
 * session was ended by a login on another device (concurrent-session cap).
 * It rides the JSON body rather than a header because the refresh endpoint
 * is cross-origin and the CORS filter exposes only a fixed header allow-list.
 */
export const SESSION_EVICTED_REASON = "SESSION_EVICTED";

/**
 * Sentinel error thrown when refresh has terminally failed. The
 * HttpService response interceptor uses this to decide whether to
 * redirect to login (terminal) versus pass the error through.
 *
 * {@link reason} carries the BE's machine-readable cause when the terminal
 * 401 named one (currently only {@link SESSION_EVICTED_REASON}); it is
 * undefined for ordinary expiry/replay refusals.
 */
export class TerminalRefreshError extends Error {
  readonly terminal = true as const;
  constructor(
    message: string,
    readonly cause?: unknown,
    readonly reason?: string,
  ) {
    super(message);
    this.name = "TerminalRefreshError";
  }
}

export function isTerminalRefreshError(e: unknown): e is TerminalRefreshError {
  return e instanceof TerminalRefreshError;
}

/**
 * Single-flight coordinator for the BE refresh endpoint.
 *
 * Concurrent callers share one outstanding /user/refresh POST. The
 * BE reads the REFRESH_TOKEN cookie via {@code withCredentials: true};
 * no Authorization header is sent.
 *
 * Circular-dependency note: this class creates its OWN axios instance
 * rather than going back through HttpService. HttpService's response
 * interceptor invokes RefreshCoordinator on 401 — routing the refresh
 * call back through HttpService would set up a refresh-on-refresh
 * infinite loop. A dedicated axios instance with no shared interceptors
 * is the simplest break of that cycle.
 */
export class RefreshCoordinator {
  /** Cross-tab lock name — one per application tab, not per instance. */
  private static readonly LOCK_NAME = "auth-refresh";
  private inFlight: Promise<void> | undefined;
  private readonly axios: AxiosInstance;
  private readonly refreshUrl: string;

  /** Retry-After cap, per BE contract — see plan slice B. */
  private static readonly RETRY_AFTER_MAX_MS = 1000;
  /**
   * Default delay applied to a 503 with no Retry-After header. The BE
   * contract is "503 + Retry-After:1", so a header-less 503 is most likely
   * an infra-layer 503 (proxy / load balancer). A small delay avoids
   * stampeding the upstream while still keeping the retry fast.
   */
  private static readonly DEFAULT_503_RETRY_DELAY_MS = 200;

  constructor(
    private readonly baseUrl: string,
    private readonly jwtStore: JwtStore,
    axiosInstance?: AxiosInstance,
  ) {
    this.axios =
      axiosInstance ??
      axios.create({
        headers: {
          "Content-type": "application/json",
        },
      });
    this.refreshUrl = `${this.baseUrl}/user/refresh`;
  }

  refresh(): Promise<void> {
    if (this.inFlight) {
      return this.inFlight;
    }
    this.inFlight = this.run().finally(() => {
      this.inFlight = undefined;
    });
    return this.inFlight;
  }

  private async run(): Promise<void> {
    if (typeof navigator !== "undefined" && navigator.locks?.request) {
      await navigator.locks.request(RefreshCoordinator.LOCK_NAME, () => this.postWithRetry());
      return;
    }
    await this.postWithRetry();
  }

  private async postWithRetry(): Promise<void> {
    try {
      await this.postOnce();
    } catch (firstError) {
      const retryAfterMs = this.parseRetryAfter(firstError);
      if (retryAfterMs === undefined) {
        // Non-503 (or unparseable Retry-After) — terminal.
        const reason = this.handleTerminal(firstError);
        throw new TerminalRefreshError("Refresh failed", firstError, reason);
      }
      // 503 — retry exactly once after Retry-After.
      await this.delay(retryAfterMs);
      try {
        await this.postOnce();
      } catch (secondError) {
        const reason = this.handleTerminal(secondError);
        throw new TerminalRefreshError("Refresh failed after retry", secondError, reason);
      }
    }
  }

  private async postOnce(): Promise<void> {
    const response = await this.axios.post<AccessTokenResponse>(this.refreshUrl, undefined, {
      withCredentials: true,
    });
    this.jwtStore.setJwt(response.data?.accessToken);
  }

  private parseRetryAfter(error: unknown): number | undefined {
    const axiosError = error as AxiosError;
    if (axiosError?.response?.status !== 503) {
      return undefined;
    }
    // Axios normalises response headers to lowercase; lookup only needs the
    // lowercase key.
    const header = axiosError.response.headers?.["retry-after"];
    if (header === undefined || header === null) {
      // 503 without Retry-After — likely an infra-layer 503. Apply a small
      // default delay so we don't stampede the upstream on retry.
      return RefreshCoordinator.DEFAULT_503_RETRY_DELAY_MS;
    }
    const seconds = Number.parseInt(String(header), 10);
    if (Number.isNaN(seconds) || seconds < 0) {
      return RefreshCoordinator.DEFAULT_503_RETRY_DELAY_MS;
    }
    return Math.min(seconds * 1000, RefreshCoordinator.RETRY_AFTER_MAX_MS);
  }

  /**
   * Clears the JWT for every terminal outcome (including a post-retry 503) so
   * the SPA stops sending a stale Bearer token. A 401 is the BE's terminal
   * signal that the session is invalidated; its body may name why the session
   * ended, which is returned so the caller can carry it on the thrown error.
   */
  private handleTerminal(error: unknown): string | undefined {
    this.jwtStore.clear();
    const axiosError = error as AxiosError;
    if (axiosError?.response?.status === 401) {
      return this.parseEvictionReason(axiosError.response.data);
    }
    return undefined;
  }

  /**
   * The eviction reason counts only when the body genuinely parses to the
   * expected shape. An ordinary 401 carries an empty or non-JSON body, which
   * must read as "no reason" — never as an eviction and never as a throw.
   */
  private parseEvictionReason(data: unknown): string | undefined {
    let reason: unknown;
    if (typeof data === "string") {
      try {
        reason = (JSON.parse(data) as { reason?: unknown }).reason;
      } catch {
        return undefined;
      }
    } else if (data && typeof data === "object") {
      reason = (data as { reason?: unknown }).reason;
    }
    return reason === SESSION_EVICTED_REASON ? SESSION_EVICTED_REASON : undefined;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
